import { type InputHTMLAttributes, type Ref, useId } from 'react';
import { cn } from '../../lib/utils';
import { fieldClass } from './fieldClass';
import { FieldError } from './FieldError';
import { FIELD_LABEL_TYPOGRAPHY } from './FieldLabel';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  /**
   * `default` - standard bordered input with padding and focus ring.
   * `ghost`   - borderless, transparent background, no padding, no focus ring.
   *             Used for in-place editable text in inline edit rows where the
   *             input must visually match the read-mode value it replaces.
   * `heading` - card-heading typography (serif, bold, `text-xl sm:text-2xl`)
   *             in a bordered box sized to sit exactly where the heading was.
   *             For a card whose title is edited in place: its padding is
   *             cancelled by a negative margin so the text keeps the heading's
   *             own left edge, and its line-height is pinned to the `h1`'s
   *             (`leading-7 sm:leading-8`) because a browser's default
   *             `line-height: normal` on an input is a few px shorter - enough
   *             to lift every row beneath it when the editor opens.
   * `toolbar` - `h-11` on a phone, `h-9` from `sm`, same border/background
   *             as `default`, for a report toolbar row (search box beside
   *             `FilterChip`, `RadioDropdown` and `ReportRangeControl`, which
   *             share that height) so the row reads as one set of controls
   *             at one height.
   */
  variant?: 'default' | 'ghost' | 'heading' | 'toolbar';
}

export function Input({ ref, className, label, error, id, variant = 'default', ...props }: InputProps & { ref?: Ref<HTMLInputElement> }) {
  const isGhost = variant === 'ghost';
  const isHeading = variant === 'heading';
  const isToolbar = variant === 'toolbar';
  const autoId = useId();
  const errorId = `${id ?? autoId}-error`;
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={id} className={`${FIELD_LABEL_TYPOGRAPHY} text-secondary`}>
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={id}
        className={cn(
          'w-full text-sm text-secondary',
          'placeholder:text-text-light',
          'disabled:cursor-not-allowed disabled:opacity-50',
          isGhost
            ? 'border-0 bg-transparent p-0 leading-5 focus:outline-none focus:ring-0'
            : isHeading
            ? cn(
                '-mx-2 -my-px rounded-md border bg-bg-primary px-2',
                'font-serif text-xl font-bold leading-7 text-secondary sm:text-2xl sm:leading-8',
                'transition-colors hover:border-primary/50',
                'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                error ? 'border-error' : 'border-border',
              )
            : cn(
                fieldClass({ error: Boolean(error) }),
                'px-3',
                isToolbar ? 'h-11 sm:h-9' : 'py-2',
              ),
          className,
        )}
        {...props}
        aria-invalid={error ? true : props['aria-invalid']}
        aria-describedby={error ? [errorId, props['aria-describedby']].filter(Boolean).join(' ') : props['aria-describedby']}
      />
      <FieldError id={errorId} error={error} />
    </div>
  );
}
